import { useEffect, useMemo, useState } from "react";
import axios from "../axios";
import { useNavigate } from "react-router-dom";

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  const [searchText, setSearchText] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [interestFilter, setInterestFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const navigate = useNavigate();

  const fetchContacts = async () => {
    const res = await axios.get("/contacts");
    setContacts(res.data);
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  // ✅ ADD CONTACT
  const addContact = async () => {
    if (!name || !phone) return alert("Enter all fields");

    await axios.post("/contacts", { name, phone });

    setName("");
    setPhone("");
    fetchContacts();
  };

  // ✅ CSV UPLOAD
  const uploadCSV = async (e) => {
    const file = e.target.files[0];

    const formData = new FormData();
    formData.append("file", file);

    await axios.post("/contacts/upload", formData);

    alert("CSV Uploaded 🚀");
    fetchContacts();
  };

  // ----- Distinct filter options, derived from current contacts -----
  const cityOptions = useMemo(
    () => [...new Set(contacts.map((c) => c.city).filter(Boolean))].sort(),
    [contacts]
  );
  const interestOptions = useMemo(
    () => [...new Set(contacts.map((c) => c.service_interest).filter(Boolean))].sort(),
    [contacts]
  );
  const statusOptions = useMemo(
    () => [...new Set(contacts.map((c) => c.lead_status).filter(Boolean))].sort(),
    [contacts]
  );

  // ----- Filtered list shown + used for bulk actions -----
  const filteredContacts = useMemo(() => {
    return contacts.filter((c) => {
      if (cityFilter && c.city !== cityFilter) return false;
      if (interestFilter && c.service_interest !== interestFilter) return false;
      if (statusFilter && c.lead_status !== statusFilter) return false;

      if (searchText.trim()) {
        const q = searchText.trim().toLowerCase();
        const haystack = `${c.name || ""} ${c.phone || ""} ${c.city || ""} ${c.service_interest || ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      return true;
    });
  }, [contacts, cityFilter, interestFilter, statusFilter, searchText]);

  const clearFilters = () => {
    setSearchText("");
    setCityFilter("");
    setInterestFilter("");
    setStatusFilter("");
  };

  // select single
  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  // select all (respects current filters)
  const selectAll = () => {
    const visibleIds = filteredContacts.map((c) => c.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));

    if (allSelected) {
      setSelected((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  };

  // delete single
  const deleteOne = async (id) => {
    await axios.delete(`/contacts/${id}`);
    fetchContacts();
  };

  // delete multiple
  const deleteSelected = async () => {
    if (selected.length === 0) {
      alert("No contacts selected");
      return;
    }

    await axios.post("/contacts/delete-multiple", {
      ids: selected
    });

    setSelected([]);
    fetchContacts();
  };

  const visibleIds = filteredContacts.map((c) => c.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold mb-4">Contacts</h1>

      {/* 🔥 ADD CONTACT */}
      <div className="bg-white p-4 rounded shadow mb-4 flex flex-wrap gap-2">
        <input
          className="border p-2"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <input
          className="border p-2"
          placeholder="Phone (91XXXXXXXXXX)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />

        <button
          onClick={addContact}
          className="bg-blue-500 text-white px-4 py-2 rounded"
        >
          Add
        </button>

        {/* CSV Upload */}
        <input type="file" onChange={uploadCSV} />
      </div>

      {/* 🔍 Filters — City / Interest / Status / Search */}
      <div className="bg-white p-4 rounded shadow mb-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-gray-700">Filter Contacts (bulk-send targeting ke liye)</p>
          {(searchText || cityFilter || interestFilter || statusFilter) && (
            <button onClick={clearFilters} className="text-xs text-blue-600 underline">
              Clear filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            className="border p-2 rounded"
            placeholder="Search name/phone/city..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <select className="border p-2 rounded" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
            <option value="">All Cities</option>
            {cityOptions.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>

          <select className="border p-2 rounded" value={interestFilter} onChange={(e) => setInterestFilter(e.target.value)}>
            <option value="">All Interests</option>
            {interestOptions.map((interest) => (
              <option key={interest} value={interest}>{interest}</option>
            ))}
          </select>

          <select className="border p-2 rounded" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Lead Statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <p className="text-xs text-gray-500 mt-2">
          Showing {filteredContacts.length} of {contacts.length} contacts
        </p>
      </div>

      {/* Actions */}
      <div className="mb-4 flex flex-wrap gap-4">
        <button
          onClick={selectAll}
          className="bg-blue-500 text-white px-4 py-2 rounded"
        >
          {allVisibleSelected ? "Unselect Filtered" : "Select All (Filtered)"}
        </button>

        <button
          onClick={deleteSelected}
          className="bg-red-500 text-white px-4 py-2 rounded"
        >
          Delete Selected ({selected.length})
        </button>

        <button
          onClick={() => navigate("/campaign", { state: { selected } })}
          className="bg-green-500 text-white px-4 py-2 rounded"
        >
          Send Campaign ({selected.length})
        </button>
      </div>

      {/* Table (desktop) / Cards (mobile) */}
      <div className="bg-white rounded shadow overflow-hidden">
        {/* Desktop header */}
        <div className="hidden md:grid grid-cols-[auto_1.2fr_1fr_0.8fr_1fr_0.8fr_auto] gap-3 px-4 py-2 border-b text-xs font-semibold text-gray-500 uppercase bg-gray-50">
          <span></span>
          <span>Name</span>
          <span>Phone</span>
          <span>City</span>
          <span>Interest</span>
          <span>Lead Status</span>
          <span></span>
        </div>

        {filteredContacts.map((c) => (
          <div key={c.id} className="border-b last:border-b-0">
            {/* Desktop row */}
            <div className="hidden md:grid grid-cols-[auto_1.2fr_1fr_0.8fr_1fr_0.8fr_auto] gap-3 items-center py-2.5 px-4">
              <input
                type="checkbox"
                checked={selected.includes(c.id)}
                onChange={() => toggleSelect(c.id)}
              />
              <p className="font-bold truncate">{c.name}</p>
              <p className="text-gray-600 truncate">{c.phone}</p>
              <p className="text-gray-600 truncate">{c.city || "-"}</p>
              <p className="text-gray-600 truncate">{c.service_interest || "-"}</p>
              <p>
                {c.lead_status ? (
                  <span className="text-xs font-semibold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                    {c.lead_status}
                  </span>
                ) : (
                  <span className="text-gray-400 text-xs">-</span>
                )}
              </p>
              <button onClick={() => deleteOne(c.id)} className="text-red-500 text-sm">
                Delete
              </button>
            </div>

            {/* Mobile compact card — everything in one tidy line, wraps if needed */}
            <div className="md:hidden flex items-start gap-2.5 px-3 py-2.5">
              <input
                type="checkbox"
                className="mt-1 flex-shrink-0"
                checked={selected.includes(c.id)}
                onChange={() => toggleSelect(c.id)}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-bold text-sm truncate">{c.name}</p>
                  <button
                    onClick={() => deleteOne(c.id)}
                    className="text-red-500 text-xs flex-shrink-0"
                  >
                    Delete
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {c.phone}
                  {c.city ? ` • ${c.city}` : ""}
                  {c.service_interest ? ` • ${c.service_interest}` : ""}
                </p>
                {c.lead_status && (
                  <span className="inline-block mt-1 text-[10px] font-semibold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                    {c.lead_status}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}

        {!filteredContacts.length && (
          <p className="text-center text-gray-400 py-6 text-sm">Koi contact nahi mila is filter ke liye.</p>
        )}
      </div>
    </div>
  );
}